import { useCallback, useEffect, useRef, useState } from 'react';
import logger from '../utils/logger';
import supabase from '../utils/supabaseClient';

/**
 * Custom hook for real-time confirmed recommendations count via Supabase Realtime.
 *
 * Fetches the count of recommendations with data_fidelity='confirmed' for the current user
 * and subscribes to real-time updates when recommendations are added, updated, or deleted.
 * Used to conditionally show/hide "My Reports" navigation menu entry.
 *
 * @param {Object} session - The Supabase session object for authentication
 * @returns {Object} - { count: number, isLoading: boolean, error: object|null }
 */
export const useConfirmedRecommendationCount = (session) => {
  const [count, setCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const channelRef = useRef(null);

  // Helper to detect if an error is an auth/JWT error
  const isAuthError = useCallback((err) => {
    if (!err) return false;
    const errorMessage = err.message || err.toString();
    return (
      errorMessage.includes('JWT') ||
      errorMessage.includes('Token has expired') ||
      errorMessage.includes('Invalid token') ||
      err.code === 'PGRST301' || // PostgREST JWT error
      err.code === '401'
    );
  }, []);

  // Helper to check if error is auth-related and set error state
  const handleFetchError = useCallback(
    (fetchError) => {
      if (isAuthError(fetchError)) {
        const authError = new Error(fetchError.message || 'Session expired. Please log in again.');
        authError.isAuthError = true;
        authError.originalError = fetchError;
        setError(authError);
      } else {
        setError(fetchError);
      }
    },
    [isAuthError]
  );

  // Fetch confirmed recommendations count
  const fetchCount = useCallback(async () => {
    if (!session?.user) {
      logger.log('[useConfirmedRecommendationCount] No session, returning count = 0');
      return 0;
    }

    logger.log('[useConfirmedRecommendationCount] Fetching confirmed recommendations count');

    const { count: fetchedCount, error: fetchError } = await supabase
      .from('user_model_fit_scores')
      .select('*', { count: 'exact', head: true })
      .eq('user_id', session.user.id)
      .eq('data_fidelity', 'confirmed');

    if (fetchError) {
      logger.error('[useConfirmedRecommendationCount] Error fetching count:', fetchError);
      handleFetchError(fetchError);
      return 0; // Default to 0 on error (graceful degradation)
    }

    logger.log('[useConfirmedRecommendationCount] Count fetched:', fetchedCount);
    return fetchedCount || 0;
  }, [session, handleFetchError]);

  // Main effect for subscribing to real-time updates
  useEffect(() => {
    if (!session?.user) {
      setCount(0);
      setIsLoading(false);
      return;
    }

    let isMounted = true;

    // Fetch initial count immediately (don't wait for subscription)
    const loadInitialCount = async () => {
      const initialCount = await fetchCount();

      // Only update state if component is still mounted
      if (isMounted) {
        setCount(initialCount);
        setIsLoading(false);
      }
    };

    loadInitialCount();

    // Set up realtime subscription for updates
    const channelName = `confirmed-recs-count-${session.user.id}`;

    // RACE CONDITION FIX: Check if channel already exists and remove it
    const existingChannels = supabase.getChannels();
    const existingChannel = existingChannels.find((ch) => ch.topic === `realtime:${channelName}`);
    if (existingChannel) {
      logger.log(
        `[useConfirmedRecommendationCount] Found existing channel "${channelName}", removing it`
      );
      supabase.removeChannel(existingChannel);
    }

    const channel = supabase.channel(channelName);
    channelRef.current = channel;

    const handleRecommendationChange = async (payload) => {
      logger.log('[useConfirmedRecommendationCount] Received update:', {
        eventType: payload.eventType,
        hasNewData: !!payload.new,
        hasOldData: !!payload.old,
        newDataKeys: payload.new ? Object.keys(payload.new) : [],
        oldDataKeys: payload.old ? Object.keys(payload.old) : [],
      });

      const eventType = payload.eventType;
      const newData = payload.new;
      const oldData = payload.old;

      // Log the actual data for debugging
      logger.log('[useConfirmedRecommendationCount] Payload details:', {
        eventType,
        oldData_fidelity: oldData?.data_fidelity,
        newData_fidelity: newData?.data_fidelity,
        oldData_structure: oldData,
        newData_structure: newData,
      });

      // Determine if count needs to be updated based on event type and data_fidelity
      let shouldRefetch = false;

      if (eventType === 'INSERT' && newData?.data_fidelity === 'confirmed') {
        // New confirmed recommendation added
        shouldRefetch = true;
        logger.log('[useConfirmedRecommendationCount] New confirmed recommendation inserted');
      } else if (eventType === 'UPDATE') {
        // Check if data_fidelity changed to/from 'confirmed'
        const wasConfirmed = oldData?.data_fidelity === 'confirmed';
        const isConfirmed = newData?.data_fidelity === 'confirmed';

        logger.log('[useConfirmedRecommendationCount] UPDATE event analysis:', {
          wasConfirmed,
          isConfirmed,
          willRefetch: wasConfirmed !== isConfirmed,
        });

        if (wasConfirmed !== isConfirmed) {
          // Status changed - need to update count
          shouldRefetch = true;
          logger.log('[useConfirmedRecommendationCount] Recommendation data_fidelity changed:', {
            from: oldData?.data_fidelity,
            to: newData?.data_fidelity,
          });
        }
      } else if (eventType === 'DELETE' && oldData?.data_fidelity === 'confirmed') {
        // Confirmed recommendation deleted
        shouldRefetch = true;
        logger.log('[useConfirmedRecommendationCount] Confirmed recommendation deleted');
      }

      // Refetch count if needed
      if (shouldRefetch && isMounted) {
        const updatedCount = await fetchCount();
        if (isMounted) {
          setCount(updatedCount);
          logger.log('[useConfirmedRecommendationCount] Count updated to:', updatedCount);
        }
      }
    };

    channel
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_model_fit_scores',
          filter: `user_id=eq.${session.user.id}`,
        },
        handleRecommendationChange
      )
      .subscribe((status, err) => {
        if (err) {
          logger.error('[useConfirmedRecommendationCount] Subscription error:', err);
          if (isAuthError(err)) {
            const authError = new Error(err.message || 'Session expired. Please log in again.');
            authError.isAuthError = true;
            authError.originalError = err;
            if (isMounted) {
              setError(authError);
            }
          }
        }

        if (status === 'SUBSCRIBED') {
          logger.log(
            '[useConfirmedRecommendationCount] Subscribed to recommendation updates for user:',
            session.user.id
          );
        }
      });

    // Cleanup subscription on unmount or when dependencies change
    return () => {
      isMounted = false;
      if (channelRef.current) {
        logger.log('[useConfirmedRecommendationCount] Unsubscribing from recommendation updates');
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [session?.user, fetchCount, isAuthError]);

  return {
    count,
    isLoading,
    error,
  };
};
